import axios, { type AxiosError } from 'axios';

import type { Opts } from '../types/common';
import type { HttpError } from '../types/error';
import type { ApiResponse } from '../types/resource';

import { instance } from './config';

/**
 * Преобразует ошибку axios в типизированную HttpError
 */
function toHttpError(e: unknown): HttpError {
  if (axios.isAxiosError(e)) {
    const err = e as AxiosError<{
      message?: string;
      error?: string;
    }>;

    const responseData = err.response?.data as { message?: string; error?: string } | undefined;

    const message = responseData?.message ?? responseData?.error ?? err.message ?? 'Network error';
    const status = err.response?.status;

    const httpError = new Error(message) as HttpError;
    httpError.status = status;
    httpError.data = err.response?.data;

    return httpError;
  }

  return new Error('Unknown error') as HttpError;
}

/**
 * HTTP GET запрос
 */
async function get<T>(url: string, opts?: Opts): Promise<ApiResponse<T>> {
  try {
    const res = await instance.get<ApiResponse<T>>(url, {
      params: opts?.query,
      signal: opts?.signal,
      headers: opts?.headers,
    });

    return res.data;
  } catch (e) {
    throw toHttpError(e);
  }
}

/**
 * HTTP POST запрос
 */
async function post<T>(url: string, body?: unknown, opts?: Opts): Promise<ApiResponse<T>> {
  try {
    const res = await instance.post<ApiResponse<T>>(url, body, {
      params: opts?.query,
      signal: opts?.signal,
      headers: opts?.headers,
    });

    return res.data;
  } catch (e) {
    throw toHttpError(e);
  }
}

/**
 * HTTP PUT запрос
 */
async function put<T>(url: string, body?: unknown, opts?: Opts): Promise<ApiResponse<T>> {
  try {
    const res = await instance.put<ApiResponse<T>>(url, body, {
      params: opts?.query,
      signal: opts?.signal,
      headers: opts?.headers,
    });

    return res.data;
  } catch (e) {
    throw toHttpError(e);
  }
}

/**
 * HTTP PATCH запрос
 */
async function patch<T>(url: string, body?: unknown, opts?: Opts): Promise<ApiResponse<T>> {
  try {
    const res = await instance.patch<ApiResponse<T>>(url, body, {
      params: opts?.query,
      signal: opts?.signal,
      headers: opts?.headers,
    });

    return res.data;
  } catch (e) {
    throw toHttpError(e);
  }
}

/**
 * HTTP DELETE запрос
 */
async function del<T>(url: string, opts?: Opts): Promise<ApiResponse<T>> {
  try {
    const res = await instance.delete<ApiResponse<T>>(url, {
      params: opts?.query,
      signal: opts?.signal,
      headers: opts?.headers,
    });

    return res.data;
  } catch (e) {
    throw toHttpError(e);
  }
}

/**
 * HTTP-клиент с методами для всех типов запросов
 * Автоматически обрабатывает ошибки и возвращает данные в формате Laravel Resource { data, meta }
 *
 * @example
 * import { http } from '@/shared/api';
 *
 * // GET запрос
 * const response = await http.get<User[]>('/users');
 * console.log(response.data); // User[]
 * console.log(response.meta); // { current_page, total, ... }
 *
 * // POST запрос
 * const response = await http.post<User>('/users', { name: 'John' });
 * console.log(response.data); // User
 *
 * // С параметрами
 * const response = await http.get<User[]>('/users', {
 *   query: { page: 1, limit: 10 }
 * });
 */
export const http = {
  get,
  post,
  put,
  patch,
  delete: del,
  instance,
};
